import { useMemo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { startOfDay, endOfDay, isValid } from 'date-fns';
import { fetchUsers } from '../../../../../redux/actions/user';
import { activeStatus, newRoles as newRolesConfig } from '../../../../../utils/envConfig'; // Renamed to avoid conflict

const useLeadsData = (
    axios,
    {
        filterSearch,
        filterRole,
        initialRole, // Renamed from 'role' in parent to avoid confusion with internal 'role' variable if any
        filterStatus,
        filterLeadSource,
        filterLeadLabel,
        selectedFilterTags,
        selectedChannelPartners,
        rangeStart,
        rangeEnd,
        filterAgent,
        filterLeadStatus,
        newLeadsSort,
        currentPage,
        itemsPerPage,
        sortBy,
        sort,
        allowedRoles, // Prop passed from parent
        viewMode,
    }
) => {
    const dispatch = useDispatch();

    const buildLink = useMemo(() => {
        let link = `/search/user/filter?page=${currentPage}&sort_by=${sortBy}&sort_dir=${sort}&per_page=${itemsPerPage}`;

        if (newLeadsSort) link = link.concat(`&newLeads=${newLeadsSort}`);
        if (filterLeadStatus) link = link.concat(`&lead_status=${filterLeadStatus}`);

        if (rangeStart && rangeEnd && isValid(rangeStart) && isValid(rangeEnd)) {
            const fromDate = startOfDay(rangeStart).toISOString();
            const toDate = endOfDay(rangeEnd).toISOString();
            link += `&from_date=${fromDate}&to_date=${toDate}`;
        } else if (rangeStart && isValid(rangeStart)) {
            link += `&from_date=${startOfDay(rangeStart).toISOString()}`;
        } else if (rangeEnd && isValid(rangeEnd)) {
            link += `&to_date=${endOfDay(rangeEnd).toISOString()}`;
        }

        if (filterAgent) link = link.concat(`&agent_id=${filterAgent}`);

        // Use initialRole for the role passed as prop, and filterRole for the selected filter
        const currentRoleFilter = filterRole || initialRole;

        if (!currentRoleFilter && (!initialRole || !allowedRoles.includes(initialRole))) {
            const defaultRolesQuery = Object.values(newRolesConfig)
                .filter((r) => allowedRoles.includes(r))
                .map((r) => `roles[]=${r}`)
                .join('&');
            if (defaultRolesQuery) link = link.concat(`&${defaultRolesQuery}`);
        } else if (filterRole) { // if a specific role is selected in filter
            link = link.concat(`&roles[]=${filterRole}`);
        } else if (initialRole && allowedRoles.includes(initialRole)) { // if initial role is valid and no specific filterRole
            link = link.concat(`&roles[]=${initialRole}`);
        }


        if (filterSearch) link = link.concat(`&search=${encodeURIComponent(filterSearch.trim())}`);

        if (filterStatus) {
            link = link.concat(`&active=${filterStatus === activeStatus[0]}`);
        } else {
            link = link.concat(`&active=true`); // Default to active if no filterStatus
        }

        if (filterLeadSource && filterLeadSource.length > 0) {
            link = link.concat(`&lead_source_many=${filterLeadSource.map(e => encodeURIComponent(e)).join(',')}`);
        }

        if (selectedFilterTags && selectedFilterTags.length > 0) {
            const tagNames = selectedFilterTags.map(tag => tag.label);
            tagNames.forEach(tag => {
                link = link.concat(`&tags=${encodeURIComponent(tag)}`);
            })
        }

        if (selectedChannelPartners && selectedChannelPartners.length > 0) {
            selectedChannelPartners.forEach(partner => {
                link = link.concat(`&partner[]=${partner.value}`);
            });
        }

        if (viewMode === "channelPartner") {
            link = link.concat(`&partnersOnly=true`);
        }
        // Note: filterLeadLabel logic that implies sources might need to be handled before calling this hook
        // or this hook needs to be aware of how to translate filterLeadLabel to source IDs if that's how the API expects it.
        // For now, buildLink reflects explicit filters.

        return link;
    }, [
        filterSearch, filterRole, initialRole, filterStatus, filterLeadSource, filterLeadLabel,
        selectedFilterTags, selectedChannelPartners, rangeStart, rangeEnd, itemsPerPage,
        sort, sortBy, currentPage, filterAgent, filterLeadStatus, newLeadsSort, allowedRoles, viewMode
    ]);

    const fetchUsersDebounced = useCallback(
        debounce((axiosInstance, linkToFetch) => {
            dispatch(fetchUsers(axiosInstance, linkToFetch));
        }, 300),
        [dispatch]
    );

    const { usersList, leadsLoading } = useSelector((state) => ({
        usersList: state?.user?.users,
        leadsLoading: state?.user?.leadsLoading,
    }));

    useEffect(() => {
        if (axios && buildLink) { // Ensure axios and buildLink are available
            fetchUsersDebounced(axios, buildLink);
        }
    }, [buildLink, axios, fetchUsersDebounced]);

    const users = usersList?.findUsersOutput || [];
    const filCount = usersList?.filteredCount || 0;

    return {
        users,
        isLoading: leadsLoading,
        filCount,
        buildLink, // Optional: for debugging or other uses
    };
};

export default useLeadsData;